import { useEffect, useRef, useState } from 'react';
import { SiGoogle, SiJirasoftware } from 'react-icons/si';

import { Button } from '@/components/animate-ui/components/buttons/button.tsx';
import { Input } from '@/components/ui/input';
import { IntegrationService } from '@/shared/api/services/integrations-service';
import { HStack, TypographyP, VStack } from '@/shared/ui';

import { StatusDot } from '../status-dot';

// CONFIG

const INTEGRATIONS_CONFIG = {
  google: {
    label: 'Google Calendar',
    fields: {
      client_id: 'Client ID',
      client_secret: 'Client Secret',
    },
  },
  jira: {
    label: 'Jira',
    fields: {
      username: 'Email',
      password: 'API Token',
      url: 'Jira URL',
      is_cloud: 'Cloud Jira',
    },
  },
} as const;

type IntegrationType = keyof typeof INTEGRATIONS_CONFIG;
type ActionType = 'create' | 'update' | null;
type IntegrationStatus = {
  connected: boolean;
  oauth_completed?: boolean;
  url?: string;
  username?: string;
};

export const AddIntegrationsModal = () => {
  const [activeIntegration, setActiveIntegration] = useState<IntegrationType | null>(null);
  const [action, setAction] = useState<ActionType>(null);
  const [form, setForm] = useState<Record<string, string | boolean>>({});
  const [loading, setLoading] = useState(false);
  const [integrationStatus, setIntegrationStatus] = useState<
    Record<IntegrationType, IntegrationStatus | null>
  >({
    google: null,
    jira: null,
  });

  const resetForm = () => {
    setActiveIntegration(null);
    setAction(null);
    setForm({});
  };

  const handleSubmit = async () => {
    if (!activeIntegration || !action) return;

    try {
      setLoading(true);

      let payload = { ...form };

      if (activeIntegration === 'google') {
        payload = {
          ...payload,
          redirect_uri: 'https://protocolist-server.flowai.ru/auth/google/callback', //window.location.origin,
          timezone: Intl.DateTimeFormat().resolvedOptions().timeZone,
        };
      }

      if (action === 'create') {
        await IntegrationService.create(activeIntegration, payload);

        // уходим на OAuth
        if (activeIntegration === 'google') {
          const redirectUrl = window.location.origin + window.location.pathname;
          // console.log('redirect_url:', redirectUrl);

          const { data } = await IntegrationService.getGoogleOauthUrl(redirectUrl);
          // setTimeout(() => {
          window.location.href = data.authorization_url;
          // }, 30000);
          return;
        }
      }

      if (action === 'update') {
        await IntegrationService.update(activeIntegration, payload);
      }

      resetForm();

      // Обновляем статус после создания/обновления интеграции
      await fetchIntegrationStatus(activeIntegration);
    } finally {
      setLoading(false);
    }
  };

  const handleAuth = async () => {
    try {
      const redirectUrl = window.location.origin + window.location.pathname;
      // console.log('redirect_url:', redirectUrl);

      const { data } = await IntegrationService.getGoogleOauthUrl(redirectUrl);
      // setTimeout(() => {
      window.location.href = data.authorization_url;
      // }, 30000);
    } catch (e) {
      console.log(e);
    }
  };

  const fetchedRef = useRef(false);

  // Функция для получения статуса интеграции
  const fetchIntegrationStatus = async (integrationType: IntegrationType) => {
    try {
      const response = await IntegrationService.getStatus(integrationType);
      // console.log(`${integrationType} status:`, response.data);

      setIntegrationStatus((prev) => ({
        ...prev,
        [integrationType]: response.data,
      }));

      return response.data;
    } catch (error) {
      console.error(`Error fetching ${integrationType} status:`, error);
      setIntegrationStatus((prev) => ({
        ...prev,
        [integrationType]: { connected: false },
      }));
      return { connected: false };
    }
  };

  // Функция для загрузки всех статусов
  const loadAllIntegrationStatuses = async () => {
    try {
      // Загружаем статусы для всех поддерживаемых интеграций
      const promises = (Object.keys(INTEGRATIONS_CONFIG) as IntegrationType[]).map(
        async (integration) => {
          try {
            const response = await IntegrationService.getStatus(integration);
            // console.log(`${integration} status loaded:`, response.data);
            return { integration, status: response.data };
          } catch (error) {
            console.error(`Failed to load ${integration} status:`, error);
            return { integration, status: { connected: false } };
          }
        },
      );

      const results = await Promise.all(promises);

      const newStatuses: Record<IntegrationType, IntegrationStatus> = {
        google: { connected: false },
        jira: { connected: false },
      };

      results.forEach(({ integration, status }) => {
        newStatuses[integration] = status;
      });

      setIntegrationStatus(newStatuses);
    } catch (error) {
      console.error('Error loading integration statuses:', error);
    }
  };

  useEffect(() => {
    if (fetchedRef.current) return;
    fetchedRef.current = true;

    loadAllIntegrationStatuses();
  }, []);

  // Функция для удаления интеграции
  const handleDeleteIntegration = async (integrationType: IntegrationType) => {
    try {
      await IntegrationService.delete(integrationType);

      // Обновляем статус после удаления
      setIntegrationStatus((prev) => ({
        ...prev,
        [integrationType]: false,
      }));

      // Если была открыта форма для этой интеграции - закрываем ее
      if (activeIntegration === integrationType) {
        resetForm();
      }
    } catch (error) {
      console.error(`Error deleting ${integrationType}:`, error);
    }
  };

  return (
    <VStack max align="start" gap="16">
      <TypographyP>Интеграции</TypographyP>

      {/* SERVICES LIST */}
      {(Object.keys(INTEGRATIONS_CONFIG) as IntegrationType[]).map((integration) => (
        <VStack key={integration} max className="rounded border p-6 gap-2">
          <VStack className="!items-center w-full gap-2">
            <HStack className="gap-2">
              {integration === 'google' ? (
                <SiGoogle />
              ) : integration === 'jira' ? (
                <SiJirasoftware />
              ) : null}
              <TypographyP className="!m-0">{INTEGRATIONS_CONFIG[integration].label}</TypographyP>
              <StatusDot
                integration={integration}
                connected={integrationStatus[integration]?.connected}
                oauth_completed={integrationStatus[integration]?.oauth_completed}
              />
            </HStack>

            <HStack gap="8">
              {!integrationStatus[integration]?.connected && (
                <Button
                  onClick={() => {
                    setActiveIntegration(integration);
                    setAction('create');
                    setForm({});
                  }}
                >
                  Добавить
                </Button>
              )}

              {integration === 'google' &&
                integrationStatus[integration]?.connected &&
                !integrationStatus[integration]?.oauth_completed && (
                  <Button onClick={handleAuth}>Авторизовать</Button>
                )}

              {integration === 'jira' && integrationStatus[integration]?.connected && (
                <Button
                  onClick={() => {
                    setActiveIntegration(integration);
                    setAction('update');
                    setForm({});
                  }}
                >
                  Изменить
                </Button>
              )}

              {integrationStatus[integration]?.connected && (
                <Button variant="destructive" onClick={() => handleDeleteIntegration(integration)}>
                  Удалить
                </Button>
              )}
            </HStack>
          </VStack>
          {/* FORM */}
          {activeIntegration === integration && action && (
            <VStack max gap="8" className="rounded border p-6 mt-4 bg-muted">
              <TypographyP>
                {action === 'create' ? 'Добавить' : action === 'update' ? 'Изменить' : null}{' '}
                {INTEGRATIONS_CONFIG[activeIntegration].label}
              </TypographyP>

              {Object.entries(INTEGRATIONS_CONFIG[activeIntegration].fields).map(
                ([field, label]) => {
                  if (field === 'is_cloud') {
                    return (
                      <label key={field} className="flex items-center gap-2 cursor-pointer">
                        <input
                          type="checkbox"
                          checked={Boolean(form.is_cloud)}
                          onChange={(e) =>
                            setForm((prev) => ({
                              ...prev,
                              is_cloud: e.target.checked,
                            }))
                          }
                        />
                        <span>{label}</span>
                      </label>
                    );
                  }

                  return (
                    <Input
                      key={field}
                      className="w-full h-[40px]"
                      placeholder={label}
                      value={(form[field] as string) ?? ''}
                      onChange={(e) =>
                        setForm((prev) => ({
                          ...prev,
                          [field]: e.target.value,
                        }))
                      }
                    />
                  );
                },
              )}

              <HStack justify="end" gap="8" className="mt-2">
                <Button disabled={loading} onClick={handleSubmit}>
                  {loading ? 'Отправка...' : 'Отправить'}
                </Button>
                <Button variant="ghost" onClick={resetForm}>
                  Отмена
                </Button>
              </HStack>
            </VStack>
          )}
        </VStack>
      ))}
    </VStack>
  );
};
